import { Component, OnInit, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { ReportStatuses } from 'src/app/shared/constants/report-statuses';
import { ComplianceCentralService } from '../../services';
import { prefix, REPORT_TYPES } from '../../constants';
import { Policies } from '../../../shared/constants';
import { PoliciesService } from '../../../shared/services';
import { Options } from '../../models/options';
import { hasReportsOnlyAccess } from 'src/app/shared/validators/access';

@Component({
  selector: 'compliance-central',
  templateUrl: './compliance-central.component.html',
  styleUrls: ['./compliance-central.component.scss']
})
export class ComplianceCentralComponent implements OnInit {
  getEntitiesPolicy: string[] = [Policies.GET_ENTITIES];
  getComplianceCentralPolicy: string[] = [Policies.COMPLIANCE_CENTRAL_ACCESS];

  REPORT_TYPES = REPORT_TYPES;
  activeRow: any = null;
  // Rows that are pulled in from the most recent DOM build.
  dirtyRows: Object = {};
  reportColumns: Object = {};
  @ViewChild('reportRows') reportRows: ElementRef;
  isDirty = false;
  options: Array<Options> = hasReportsOnlyAccess() ? [{
    name: 'Quick View',
    value: 'quick-view',
    policies: this.getComplianceCentralPolicy
  }] : [{
    name: 'Quick View',
    value: 'quick-view',
    policies: this.getComplianceCentralPolicy
  }, {
    name: 'Country Guide',
    value: 'country-guide',
    policies: this.getEntitiesPolicy
  }];
  // Rows will get flushed on ngDoCheck from dirtyRows to rows if changes occur.
  rows: Array<any> = [];

  @Output() rowClick = new EventEmitter();
  activeReportType: any;
  showOptions: boolean = true;
  showCounters: boolean = true;

  constructor(
    private _router: Router,
    public service: ComplianceCentralService,
    private _userPoliciesService: PoliciesService) {}

  ngOnInit() {
    this._userPoliciesService.getUserPolicies().subscribe((policies) => {
      let updatedOptions = [];
      this.options.forEach((option: Options) => {
        const update = (option.policies && policies.some(userPolicy => option.policies.includes(userPolicy.policyName)));
        if (update) {
          updatedOptions.push(option);
        }
      });
      this.options = updatedOptions;
    });
    this.onReportTypeSelected(REPORT_TYPES.localFile);
  }

  /**
   * This assigns new values to the datatable rows.  This will trigger a render
   * and it is necessary because extra DOM information is required for the
   * datatable (which of course requires at least one render within this
   * component).  It's a little bit to understand but:
   * 1) Datatable needs custom markup/HTML for Compliance Central
   * 2) This component renders that markup, marks the model as "dirty"
   * 3) This function then assigns the new data, trigger the datatable to redraw
   */
  ngDoCheck() {
    if (this.isDirty) {
      for (let idx in this.service.reportInformation) {
        Object.assign(this.rows[idx], this.dirtyRows[idx]);
      }
      this.isDirty = false;
      this.dirtyRows = [];
    }
  }
  onReportTypeSelected(reportType: string): void {
    switch (reportType) {
      case REPORT_TYPES.localFile :
        this.reportColumns = {
          country: {
            name: 'Country Name',
            searchable: true
          },
          prepareByDate: 'Due Date',
          status: 'Domicile Status'
        };
        break;
      default :
        this.reportColumns = {
          country: {
            name: 'Country Name',
            searchable: true
          },
          filingDate: 'Due Date',
          status: 'Domicile Status'
        };
    }
    this.service.initializeReportData(reportType);
    this.activeReportType = this.service.getActiveReportType();
    this.showOptions = this.showCounters = (this.activeReportType === REPORT_TYPES.localFile);
  }

  /**
   * Gets the percentage of completed reports.
   */
  getCompleted() {
    if (!this.service.counters || !this.service.counters.total) {
      return 0;
    }
    return this.service.counters.final / this.service.counters.total;
  }

  /**
   * Localized string.
   */
  getDomicileStatusTooltip(domicileStatus: number): string {
    return domicileStatus === 1 ? 'compliance_central.complete_tooltip' :
      'compliance_central.not_complete_tooltip';
  }

  /**
   * Localized string.
   */
  getDomicileStatusText(domicileStatus: number): string {
    return domicileStatus === 1 ? 'compliance_central.complete' :
      'compliance_central.not_complete';
  }

  /**
   * Gets the repository link of the entity.  This is used for downloading the
   * report.
   * @todo Hoist this to a service?
   */
  getFinalReportRepositoryLink(entityId: number): string {
    return `/entity/${entityId}/entity-repository/final-reports`;
  }

  /**
   * Gets the percentage of pending reports.
   */
  getPending() {
    if (!this.service.counters || !this.service.counters.total) {
      return 0;
    }
    return this.service.counters.pending / this.service.counters.total;
  }

  /**
   * Localized string.
   */
  getReportStatusTooltip(reportStatus: string): string {
    return reportStatus === ReportStatuses.DRAFT ?
      'compliance_central.report_status_draft_tooltip' :
      'compliance_central.report_status_final_tooltip';
  }

  /**
   * Gets the report.  Does a dirty check because the country and status
   * will need to render in the DOM of this component.
   *
   * @see ngDoCheck
   * Will render on nextTick if country OR status are changed.  This is needed,
   * else this will generate a warning because the data model has changed duringcountryName: "China"
   * the render loop.
   */
  getReport() {
    this.isDirty = false;
    this.rows = [];
    this.dirtyRows = {};
    for (let idx in this.service.reportInformation) {
      const report = this.service.reportInformation[idx];
      const row = report as any;

      // Gets the data from the DOM of this element.
      const country = this.reportRows.nativeElement
        .querySelector(`.countryName[data-index='${idx}']`);
      const status = this.service.translate(this
        .getDomicileStatusText(row.domicileStatus));

      // If the DOM data has not been set or is changed...
      if (!row.country || (row.country &&
        country &&
        country.outerHTML !== row.country.outerHTML)) {
        this.isDirty = true;
      }
      // If the DOM data has not been set or is changed...
      if (!row.status || (row.status && status !== row.status)) {
        this.isDirty = true;
      }
      this.dirtyRows[idx] = {
        country,
        status
      };

      // Push the row without the additional DOM data...
      this.rows.push(row);
    }

    return this.rows;
  }

  /**
   * Gets the row meta, notably for passing the row clas information for when a
   * report has been completed.
   */
  getReportRowMeta() {
    return this.rows.map((row) => {
      return { className: { 'completed': row.domicileStatus === 1 } };
    });
  }

  /**
   * Gets the correct class to display with.
   */
  getReportDownloadLinkClass(reportStatus: string): string {
    return this.service.userHasReportDownloadPermissions(reportStatus) ?
      'dropdown-item' : 'dropdown-item disabled';
  }

  getSelectedRow() {
    const rows = this.getReport();
    return rows.findIndex((row, idx) => {
      return this.service.activeReport && idx === this.service.activeReport.id;
    });
  }

  /**
   * Route to the country carousel.
   * @todo Delete me?
   */
  goToCountryCarousel(countryIso): void {
    if (this.service.userHasNavigationPermissions()) {
      // TODO: Uncomment me???
      // this._entitiesService.setActiveTab(2);
      this._router.navigate(['entity'], { queryParams: { country: countryIso } });
    }
  }

  /**
   * Route to the entity dashboard.
   * @todo Delete me?
   */
  goToEntityDashboard(entityId: number): void {
    if (this.service.userHasNavigationPermissions()) {
      this._router.navigate([`entity-dashboard/${entityId}`]);
    }
  }

  /**
   * Handles what happens when an option click occurs.
   * @param $event
   */
  handleOptionClick({ option, row }) {
    if (option === 'quick-view') {
      // TODO: Object assign here assists with re-draw on close/re-open..
      // but is this desirable or do we need an exact reference???
      this.service.activeReport = Object.assign({}, row);
      // TODO: Should this be cached on the service???
      this.service.getReportDetailsByJurisdiction(row.domicileId, row.isoCode, this.activeReportType);
    } else if (option === 'country-guide') {
      this._router.navigate([`${prefix}/global-guide/${row.isoCode}`]);
    }
  }
}
